import { useTranslation } from "react-i18next";
import type { AxiosResponse } from "axios";
import { useCallback } from "react";

import { notify } from "@/components/atoms/notify";
import type { IPromiseNotifProps } from "@/components/atoms/notify/types";
import type { IApiResponse } from "@/api";

type INotifyPromiseOptions<T> = Partial<
  Pick<
    IPromiseNotifProps<T>,
    "pendingMessage" | "rejectMessage" | "resolvedMessage"
  >
>;

export function useNotifyPromise<T>(
  options?: INotifyPromiseOptions<AxiosResponse<IApiResponse<T>>>,
) {
  return useCustomNotifyPromise<AxiosResponse<IApiResponse<T>>>({
    ...options,
    resolvedMessage(data) {
      return { text: data?.data.message || "" };
    },
  });
}

export function useCustomNotifyPromise<T>(
  globalOptions?: INotifyPromiseOptions<T>,
) {
  const { t } = useTranslation("form");

  return useCallback(
    (promise: Promise<T>, localOptions?: INotifyPromiseOptions<T>) => {
      const options = localOptions || globalOptions;

      return notify.promise<T>({
        promise,
        pendingMessage: options?.pendingMessage || t("isProcessing"),
        rejectMessage: options?.rejectMessage,
        resolvedMessage: (data) => {
          if (options?.resolvedMessage) {
            if (typeof options.resolvedMessage === "function")
              return options.resolvedMessage(data);
            return options.resolvedMessage;
          }
          return { text: t("successfulOperation") };
        },
      });
    },
    [globalOptions, t],
  );
}
